import { useCallback, useMemo } from 'react';

import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import { cva, cx } from 'class-variance-authority';
import * as AspectRatio from '@radix-ui/react-aspect-ratio';

import { Asset, RichText as RichTextType, Voucher } from '@oresundsbron/api';
import { Box, PageWrapper, Typography } from '@oresundsbron/bridge-ui';
import { Info } from '@oresundsbron/icons';

import { useClubOffer } from '~/hooks/useClubOffer';
import { useLocale } from '~/stores/locale';

import { SEOMetadata, CarouselV2, RichText } from '~/components';

import { Breadcrumbs } from '../Breadcrumbs';
import {
  ContactInfo,
  RedeemButton,
  VoucherTitle,
  VoucherValidUntil,
} from './components';

const bannerSection = cva([
  'z-10',
  'col-start-2',
  'text-center',
  'relative',
  'flex',
  'flex-col',
  'max-w-max',
  'mx-auto',
  'my-4',
  'sm:my-8',
  'lg:pb-16',
  'min-h-[35vh]',
  'sm:min-h-[40vh]',
  'md:min-h-[45vh]',
]);

export const OfferArticle = ({ id }: { id: string }) => {
  const { t } = useTranslation(['club', 'common'], { nsMode: 'fallback' });
  const { dateLocale } = useLocale();
  const { data, seoMetadata } = useClubOffer(id);

  const ValidityDate = useCallback(
    ({ date: dateString }: { date: string }) => (
      <VoucherValidUntil
        date={dateString}
        label={t('club.voucher.validUntilLabel')}
        locale={dateLocale?.code || 'sv-SE'}
      />
    ),
    [dateLocale, t]
  );

  const isValid = (date1String: string, date2String: string) => {
    const date1 = new Date(date1String).setHours(0, 0, 0, 0),
      date2 = new Date(date2String).setHours(0, 0, 0, 0);

    return date1 > date2 || date1 === date2 ? true : false;
  };

  const jubileeVoucher = useMemo(() => {
    const voucher = data?.jubileeVoucher;

    return isValid(voucher?.validUntil!, new Date().toString())
      ? voucher
      : null;
  }, [data]);

  const voucher = useMemo(() => {
    const voucher = data?.voucher;

    return isValid(voucher?.validUntil!, new Date().toString())
      ? voucher
      : null;
  }, [data]);

  const RenderVoucher = ({
    voucher,
    jubileeVoucher = false,
  }: {
    voucher: Voucher;
    jubileeVoucher?: boolean;
  }) => (
    <div className="items-center justify-between gap-6 sm:flex sm:text-left">
      <Box className="flex flex-col gap-1">
        <VoucherTitle title={voucher.title || t('club.fallbackTitle')} />

        {jubileeVoucher && (
          <Typography className="text-primary-200" size="md" weight="bold">
            {t('club.jubileeOffer')}
          </Typography>
        )}

        <ValidityDate date={voucher.validUntil} />
      </Box>

      {data && <RedeemButton voucher={voucher} title={data.title} />}
    </div>
  );

  const Vouchers = () => (
    <div className="grid gap-6 rounded-xl bg-primary-950 p-6 text-center">
      {jubileeVoucher && (
        <RenderVoucher
          voucher={jubileeVoucher as Voucher}
          jubileeVoucher={true}
        />
      )}

      {jubileeVoucher && voucher && (
        <div className="border-2 border-primary-700" />
      )}

      {voucher && <RenderVoucher voucher={voucher as Voucher} />}
    </div>
  );

  const Content = ({ content }: { content: RichTextType }) => {
    return content ? (
      <Box className={cx('print:hidden')}>
        <RichText content={content} />
      </Box>
    ) : null;
  };

  const Hero = ({
    data,
  }: {
    data: {
      category: { externalName: string };
      location: { externalName: string };
      title: string;
      preamble: string;
      banner: { url: string; description: string };
    };
  }) => (
    <>
      <Box className={bannerSection()}>
        <Box className="mx-0 my-4 inline-block  max-w-max rounded-xl bg-primary-700 text-left sm:my-auto">
          <div className=" max-w-4xl rounded-md bg-primary-700 p-6 text-left sm:mt-0">
            <div className="flex items-center">
              {data?.category ? (
                <Typography
                  as="span"
                  className=" text-lg text-primary-100"
                  intent="title"
                  weight="normal"
                >
                  {data.category.externalName}
                </Typography>
              ) : null}

              {data?.category && data.location && (
                <Typography
                  as="span"
                  weight="bold"
                  aria-hidden="true"
                  color="white"
                  className="mx-1 !text-2xl"
                >
                  &middot;
                </Typography>
              )}

              {data?.location ? (
                <Typography
                  as="span"
                  className="text-lg text-primary-100"
                  intent="title"
                  weight="semibold"
                >
                  {data.location.externalName}
                </Typography>
              ) : null}
            </div>
            <Typography
              color="white"
              as="h1"
              intent="headline"
              size="xl2"
              className="hyphens-auto text-balance break-words text-left"
            >
              {data?.title?.replaceAll(/-/g, '\u00AD')}
            </Typography>
            <Typography
              color="white"
              size="lg"
              className="max-w-[70ch] pt-6 text-left sm:pt-8"
            >
              {data?.preamble}
            </Typography>
          </div>
        </Box>
      </Box>

      <Box className="absolute inset-0">
        <Image
          src={data?.banner?.url || '/images/pattern-waves.png'}
          alt={data?.banner?.description || ''}
          priority
          fill
          sizes="(max-width: 1759px) 100vw,
            1760px"
          style={{
            aspectRatio: '16/9',
            objectFit: 'cover',
          }}
        />
      </Box>
    </>
  );

  const Disclaimer = ({
    data,
  }: {
    data: { disclaimer: { content: string } };
  }) => {
    return data.disclaimer ? (
      <div className="flex gap-4 text-type-secondary">
        <div>
          <Info />
        </div>
        <div>
          <Typography
            intent="label"
            size="md"
            className="font-normal text-type-secondary"
          >
            {data.disclaimer.content}
          </Typography>
        </div>
      </div>
    ) : null;
  };

  const imageData = useCallback(() => {
    return data?.images || null;
  }, [data]);

  const ClubOfferImage = ({ img, index }: { img: Asset; index?: Number }) => (
    <>
      <Image
        key={img.sys.id}
        alt={img.description || ''}
        src={img.url}
        priority={index === 0}
        sizes="(max-width: 768px) 100vw,
(max-width: 1200px) 50vw,
33vw"
        fill
        style={{
          objectFit: 'cover',
        }}
      />
    </>
  );

  const RenderClubOfferImages = () => {
    if ((imageData() as Asset[]).length > 1) {
      return (
        <CarouselV2>
          {(imageData() as Asset[]).map((img, i) => {
            return <ClubOfferImage img={img} index={i} key={img.sys.id} />;
          })}
        </CarouselV2>
      );
    } else {
      const image = (imageData() as Asset[])[0];

      if (image) {
        return (
          <Box>
            <AspectRatio.Root
              ratio={16 / 9}
              className="overflow-hidden rounded-xl"
            >
              <ClubOfferImage img={image} />
            </AspectRatio.Root>
          </Box>
        );
      } else {
        return null;
      }
    }
  };

  if (!data) {
    return null;
  }

  return (
    <div>
      <SEOMetadata items={seoMetadata} customJsonLd={data?.jsonLd} />
      <PageWrapper as="div" data-mode="club" className={cx('relative')}>
        <Hero data={data} />
      </PageWrapper>
      <div
        data-mode="club"
        className={cx([
          'flex',
          'min-h-[50vh]',
          'flex-col',
          'gap-6',
          'p-4',
          'pt-2',
          'sm:p-16',
          'sm:pt-6',
          'md:relative',
          'md:mx-auto',
          'lg:-mt-16',
          'md:max-w-7xl',
          'md:rounded-t-xl',
          'md:bg-white',
          'md:px-16',
          'md:shadow-xl',
        ])}
      >
        <Breadcrumbs className="col-span-full" id={id} />
        <RenderClubOfferImages />
        <div
          className={cx([
            'flex',
            'flex-col',
            'gap-6',
            'sm:gap-12',
            'mx-auto',
            'mt-6',
            'sm:mt-12',
            'max-w-full',
            'md:max-w-prose',
          ])}
        >
          {(jubileeVoucher || voucher) && <Vouchers />}
          <Content content={data.content!} />
          {data.contactInfo && <ContactInfo contactInfo={data.contactInfo!} />}
          <Disclaimer data={data} />
        </div>
      </div>
    </div>
  );
};
